import { getAccountByID } from "src/db/serviceAccount";
import db from "src/db";
import { getClientId, getUserId } from "src/app/modules/localstorage";
import Account from "src/app/models/Account";
import { CHAT_ENTITY_CACHE_TTL_MS } from "../config/queryLimits";
import { auditChatRead, auditDocIds } from "../utils/firestoreReadAudit";
import {
  buildChatCacheKey,
  deserializeTimestamp,
  readFreshChatCache,
  serializeTimestamp,
  writeFreshChatCache,
} from "./chatStorageCache";

type FetchContext = {
  source: string;
  collaborationId?: string;
  roomId?: string;
  messageId?: string;
  channel?: string;
};

const buildEntityCacheKey = (entity: string, id: string) => {
  const clientId = getClientId();
  const userId = getUserId();
  if (!clientId || !userId || !id) return null;
  return buildChatCacheKey(clientId, userId, entity, id);
};

const serializeValue = (value: any, depth = 0): any => {
  if (!value || typeof value !== "object") return value;
  const timestamp = serializeTimestamp(value);
  if (timestamp) return { __chatCacheTimestamp: timestamp };
  if (value.id && value.path) return { id: value.id, path: value.path };
  if (Array.isArray(value)) return value.map((item) => serializeValue(item, depth + 1));
  if (depth > 4) return undefined;

  return Object.fromEntries(
    Object.entries(value)
      .filter(([, item]) => typeof item !== "function")
      .map(([key, item]) => [key, serializeValue(item, depth + 1)])
      .filter(([, item]) => item !== undefined)
  );
};

const hydrateValue = (value: any): any => {
  if (!value || typeof value !== "object") return value;
  if (value.__chatCacheTimestamp) return deserializeTimestamp(value.__chatCacheTimestamp);
  if (Array.isArray(value)) return value.map(hydrateValue);

  return Object.fromEntries(
    Object.entries(value).map(([key, item]) => [key, hydrateValue(item)])
  );
};

const auditEntityRead = ({
  event,
  source,
  docs,
  docIds,
  startedAt,
  context,
  details,
  estimatedReads = Math.max(docs, 1),
}: {
  event: "account_fetch" | "order_fetch";
  source: string;
  docs: number;
  docIds?: string[];
  startedAt: number;
  context: FetchContext;
  details?: Record<string, any>;
  estimatedReads?: number;
}) => {
  auditChatRead({
    event,
    source,
    collaborationId: context.collaborationId,
    roomId: context.roomId,
    messageId: context.messageId,
    channel: context.channel,
    docs,
    docIds: auditDocIds(docIds ?? []),
    estimatedReads,
    elapsedMs: Date.now() - startedAt,
    details,
  });
};

export const fetchCachedAccountById = async (
  accountId: string,
  context: FetchContext
) => {
  const key = buildEntityCacheKey("account", accountId);
  const startedAt = Date.now();
  const cached = key ? readFreshChatCache<Record<string, any>>(key, CHAT_ENTITY_CACHE_TTL_MS) : null;

  if (cached) {
    auditEntityRead({
      event: "account_fetch",
      source: "account_storage_hit",
      docs: 1,
      docIds: [accountId],
      startedAt,
      context,
      details: { accountId, originalSource: context.source, cacheState: "localstorage_hit" },
      estimatedReads: 0,
    });
    return hydrateValue(cached.value) as Account;
  }

  const account = await getAccountByID(accountId);
  auditEntityRead({
    event: "account_fetch",
    source: context.source,
    docs: account ? 1 : 0,
    docIds: [accountId],
    startedAt,
    context,
    details: { accountId, exists: Boolean(account) },
  });

  if (account && key) writeFreshChatCache(key, serializeValue(account));
  return account;
};

export const fetchCachedOrderById = async (
  orderId: string,
  context: FetchContext
) => {
  const key = buildEntityCacheKey("order", orderId);
  const startedAt = Date.now();
  const cached = key ? readFreshChatCache<Record<string, any>>(key, CHAT_ENTITY_CACHE_TTL_MS) : null;

  if (cached) {
    auditEntityRead({
      event: "order_fetch",
      source: "order_storage_hit",
      docs: 1,
      docIds: [orderId],
      startedAt,
      context,
      details: { orderId, originalSource: context.source, cacheState: "localstorage_hit" },
      estimatedReads: 0,
    });
    return hydrateValue(cached.value);
  }

  const orderDoc = await db.collection("orders").doc(orderId).get();
  const order = orderDoc.exists ? { ...orderDoc.data(), id: orderDoc.id } : undefined;
  auditEntityRead({
    event: "order_fetch",
    source: context.source,
    docs: orderDoc.exists ? 1 : 0,
    docIds: [orderId],
    startedAt,
    context,
    details: { orderId, exists: orderDoc.exists },
  });

  if (order && key) writeFreshChatCache(key, serializeValue(order));
  return order;
};

export const fetchCachedOrdersBySN = async (
  orderSN: string | undefined,
  context: FetchContext
) => {
  if (!orderSN) return [];
  const key = buildEntityCacheKey("order-sn", orderSN);
  const startedAt = Date.now();
  const cached = key ? readFreshChatCache<Array<Record<string, any>>>(key, CHAT_ENTITY_CACHE_TTL_MS) : null;

  if (cached) {
    auditEntityRead({
      event: "order_fetch",
      source: "order_storage_hit",
      docs: cached.value.length,
      docIds: cached.value.map((order) => String(order.id ?? "")),
      startedAt,
      context,
      details: { orderSN, originalSource: context.source, cacheState: "localstorage_hit" },
      estimatedReads: 0,
    });
    return cached.value.map(hydrateValue);
  }

  const snap = await db.collection("orders").where("ordersn", "==", orderSN).get();
  const orders = snap.docs.map((doc) => ({ ...doc.data(), id: doc.id }));
  auditEntityRead({
    event: "order_fetch",
    source: context.source,
    docs: snap.docs.length,
    docIds: snap.docs.map((doc) => doc.id),
    startedAt,
    context,
    details: { orderSN },
  });

  if (key) writeFreshChatCache(key, orders.map(serializeValue));
  orders.forEach((order) => {
    if (order.id) {
      const orderKey = buildEntityCacheKey("order", String(order.id));
      if (orderKey) writeFreshChatCache(orderKey, serializeValue(order));
    }
  });
  return orders;
};
